/*
 * <addon-bundle> — Friends & Family add-on panels
 * ------------------------------------------------
 * Renders a quantity stepper for an add-on product and, when qty > 0,
 * rides the selected panels along in the SAME "Add to cart" request as the
 * main product — no fork of the shared product-form.js.
 *
 * How it hooks in:
 *  - Listens (capture phase) for submit on the main product's add-to-cart
 *    form(s) for this section (main column + sticky bar).
 *  - Rewrites the main line's fields (id / quantity / selling_plan /
 *    properties[...]) into an items[0][...] shape, then appends items[1..]
 *    for the add-on. Shopify's /cart/add.js accepts this multi-item body,
 *    and the untouched product-form.js serialises it verbatim.
 *  - The transform is idempotent: every submit first normalises the form
 *    back to single-line names, so repeat adds stay correct.
 *
 * Data attributes:
 *  - data-section     : owning section id (matches main + sticky forms)
 *  - data-variant-id  : add-on variant id
 *  - data-max         : max panels selectable
 */
if (!customElements.get('addon-bundle')) {
  customElements.define(
    'addon-bundle',
    class AddonBundle extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.variantId = this.dataset.variantId;
        this.max = parseInt(this.dataset.max, 10) || 10;
        this.qty = 0;

        this.input = this.querySelector('[data-addon-qty]');
        this.minusBtn = this.querySelector('[data-addon-minus]');
        this.plusBtn = this.querySelector('[data-addon-plus]');
        this.totalEl = this.querySelector('[data-addon-total]');
      }

      connectedCallback() {
        if (!this.variantId) return;

        this.minusBtn && this.minusBtn.addEventListener('click', () => this.step(-1));
        this.plusBtn && this.plusBtn.addEventListener('click', () => this.step(1));
        this.input &&
          this.input.addEventListener('change', () => {
            this.setQty(parseInt(this.input.value, 10) || 0);
          });

        // Capture-phase so we transform the form BEFORE product-form.js reads it.
        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      step(delta) {
        this.setQty(this.qty + delta);
      }

      setQty(next) {
        this.qty = Math.max(0, Math.min(this.max, next));
        if (this.input) this.input.value = this.qty;
        this.classList.toggle('addon-bundle--active', this.qty > 0);
        if (this.minusBtn) this.minusBtn.disabled = this.qty <= 0;
        if (this.plusBtn) this.plusBtn.disabled = this.qty >= this.max;
        if (this.totalEl) {
          this.totalEl.hidden = this.qty <= 0;
          const label = this.totalEl.querySelector('[data-addon-total-count]');
          if (label) label.textContent = this.qty;
        }
      }

      /* Is this add-to-cart form the main product's form for our section? */
      ownsForm(form) {
        if (!form || form.dataset.type !== 'add-to-cart-form') return false;
        // Exclude quick-add / complementary / recommendation forms.
        if (form.closest('quick-add-modal, .complementary-products, product-recommendations')) return false;
        const owner = form.closest(`#MainProduct-${this.sectionId}, #sticky-cart-${this.sectionId}`);
        return !!owner;
      }

      /* All fields belonging to this form: descendants + form= associated. */
      lineFields(form) {
        const sel = '[name="id"], [name="quantity"], [name="selling_plan"], [name^="properties["]';
        const set = new Set(form.querySelectorAll(sel));
        if (form.id) {
          document
            .querySelectorAll(`[form="${form.id}"]`)
            .forEach((el) => {
              if (el.name && el.matches(sel)) set.add(el);
            });
        }
        return set;
      }

      onSubmit(evt) {
        const form = evt.target;
        if (!this.ownsForm(form)) return;

        // Always start from a clean single-line form (idempotent across repeat adds).
        this.normalize(form);
        if (this.qty <= 0) return;

        // Promote the main product line into items[0][...], preserving
        // quantity, selling_plan and any app-added properties[...].
        this.lineFields(form).forEach((el) => {
          if (el.disabled) return;
          const name = el.getAttribute('name');
          el.setAttribute('data-addon-orig-name', name);
          if (name.indexOf('properties[') === 0) {
            const key = name.slice('properties['.length, -1);
            el.setAttribute('name', `items[0][properties][${key}]`);
          } else {
            el.setAttribute('name', `items[0][${name}]`);
          }
        });

        // Ensure a main quantity exists (buy button hides the qty field → default 1).
        if (!form.querySelector('[name="items[0][quantity]"]')) {
          this.addInput(form, 'items[0][quantity]', '1');
        }

        // Append the add-on line as items[1].
        this.addInput(form, 'items[1][id]', this.variantId);
        this.addInput(form, 'items[1][quantity]', String(this.qty));
        this.addInput(form, 'items[1][properties][_addon]', 'friends-family');

        // product-form.js builds its FormData synchronously in this same event,
        // so restore the DOM right after so variant changes / repeat adds keep
        // finding [name="id"] etc.
        setTimeout(() => this.normalize(form), 0);
      }

      /* Undo any prior transform + remove injected inputs. Global scope so
         form-associated (form=) inputs outside the form are also restored. */
      normalize(form) {
        document.querySelectorAll('[data-addon-orig-name]').forEach((el) => {
          el.setAttribute('name', el.getAttribute('data-addon-orig-name'));
          el.removeAttribute('data-addon-orig-name');
        });
        form.querySelectorAll('[data-addon-injected]').forEach((el) => el.remove());
      }

      addInput(form, name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-addon-injected', '');
        form.appendChild(input);
      }
    }
  );
}
